import {
  collection,
  documentId,
  onSnapshot,
  query,
  where,
  type Firestore,
} from "firebase/firestore";

type Data = Record<string, unknown>;
type Stop = () => void;
export interface OperationalModeSnapshot {
  roles: Data[];
  modes: Data[];
  projection: Data | undefined;
  loading: boolean;
  error: string;
}

// One subscription lifetime covers authorization, the mode list and Home projection.
// Repeated role snapshots must not tear down an already-authorized data stream.
export function watchOperationalModeWorkspace(input: {
  db: Firestore;
  organizationId: string;
  uid: string;
  onChange: (snapshot: OperationalModeSnapshot) => void;
  onDiagnostic: (phase: string, code: string) => void;
  timeoutMs?: number;
}): Stop {
  let disposed = false;
  let generation = 0;
  let childrenStarted = false;
  let modesReady = false;
  let projectionReady = false;
  let stopRoles: Stop | undefined;
  let stopModes: Stop | undefined;
  let stopProjection: Stop | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const state: OperationalModeSnapshot = {
    roles: [],
    modes: [],
    projection: undefined,
    loading: true,
    error: "",
  };
  const emit = (): void => {
    if (!disposed) input.onChange({ ...state });
  };
  const clearTimer = (): void => {
    clearTimeout(timer);
    timer = undefined;
  };
  const fail = (phase: string, error: unknown): void => {
    if (disposed) return;
    const code =
      typeof error === "object" &&
      error !== null &&
      "code" in error &&
      typeof error.code === "string"
        ? error.code
        : "mode_load_failed";
    state.loading = false;
    state.error = code;
    clearTimer();
    input.onDiagnostic(phase, code);
    emit();
  };
  const startTimer = (): void => {
    clearTimer();
    timer = setTimeout(
      () => fail("startup", { code: "mode_load_timeout" }),
      input.timeoutMs ?? 15_000,
    );
  };
  const stopChildren = (): void => {
    generation += 1;
    stopModes?.();
    stopProjection?.();
    stopModes = undefined;
    stopProjection = undefined;
    childrenStarted = false;
    modesReady = false;
    projectionReady = false;
    state.modes = [];
    state.projection = undefined;
  };
  const settle = (): void => {
    if (modesReady && projectionReady) {
      state.loading = false;
      state.error = "";
      clearTimer();
    }
    emit();
  };
  const startChildren = (): void => {
    if (childrenStarted) return;
    childrenStarted = true;
    const currentGeneration = ++generation;
    const current = (): boolean => !disposed && currentGeneration === generation;
    state.loading = true;
    state.error = "";
    startTimer();
    stopModes = onSnapshot(
      collection(input.db, "organizations", input.organizationId, "operationalModes"),
      { includeMetadataChanges: true },
      (snapshot) => {
        if (!current() || snapshot.metadata.fromCache) return;
        try {
          state.modes = snapshot.docs.map((mode) => ({ ...mode.data(), modeId: mode.id }));
          modesReady = true;
          settle();
        } catch (error) {
          fail("modes", error);
        }
      },
      (error) => {
        if (!current()) return;
        modesReady = false;
        state.modes = [];
        fail("modes", error);
      },
    );
    stopProjection = onSnapshot(
      query(
        collection(input.db, "organizations", input.organizationId, "operationalModeProjections"),
        where(documentId(), "==", "current"),
      ),
      { includeMetadataChanges: true },
      (snapshot) => {
        if (!current() || snapshot.metadata.fromCache) return;
        try {
          state.projection = snapshot.docs[0]?.data();
          projectionReady = true;
          settle();
        } catch (error) {
          fail("projection", error);
        }
      },
      (error) => {
        if (!current()) return;
        projectionReady = false;
        state.projection = undefined;
        fail("projection", error);
      },
    );
  };
  startTimer();
  emit();
  try {
    stopRoles = onSnapshot(
      collection(input.db, "organizations", input.organizationId, "roleAssignments"),
      { includeMetadataChanges: true },
      (snapshot) => {
        if (disposed || snapshot.metadata.fromCache) return;
        try {
          state.roles = snapshot.docs.map((role) => role.data());
          const authorized = state.roles.some(
            (role) =>
              role.organizationId === input.organizationId &&
              role.personId === input.uid &&
              role.status === "active" &&
              Array.isArray(role.roleIds) &&
              role.roleIds.some((id) => id === "organizationOwner" || id === "organizationAdmin"),
          );
          if (!authorized) {
            stopChildren();
            fail("roles", { code: "organization_admin_required" });
          } else {
            startChildren();
            emit();
          }
        } catch (error) {
          fail("roles", error);
        }
      },
      (error) => {
        if (disposed) return;
        state.roles = [];
        stopChildren();
        fail("roles", error);
      },
    );
  } catch (error) {
    fail("startup", error);
  }
  return () => {
    disposed = true;
    clearTimer();
    stopRoles?.();
    stopChildren();
  };
}
